import { useEffect, useState } from "react";
import { Basket } from "lib/types";
import { handleImageUpload } from "lib/utils";
import { instance, useBasket, pingAPIForResult } from "@clearabee/ui-sdk";
import { useRef } from "react";
import { CustomImage } from "@clearabee/ui-library-native";
import { UseMutationResult, useMutation } from "react-query";

interface UseBasketImagesOptions {
  uploads?: Basket["uploads"];
}

interface UseBasketImagesReturn {
  images: File[];
  basketImages: CustomImage[];
  imagesAreLoading: boolean;
  uploadLoading: boolean;
  setImages: (images: File[]) => void;
  uploadImages: UseMutationResult<void, unknown, void, unknown>;
}

export const useBasketImages = ({
  uploads,
}: UseBasketImagesOptions): UseBasketImagesReturn => {
  const [images, setImages] = useState<File[]>([]);
  const [basketImages, setBasketImages] = useState<CustomImage[]>([]);
  const [imagesAreLoading, setImagesAreLoading] = useState(!!uploads?.length);
  const [uploadLoading, setUploadLoading] = useState(false);
  const hasChanged = useRef(false);
  const { basket, fetch: refetchBasket } = useBasket();

  const uploadImages = useMutation(async (): Promise<void> => {
    if (!basket || !hasChanged.current) return;

    const imagesToRemove = basketImages.filter(
      ({ id }) => !images.some((file) => file.name.includes(id)),
    );
    await Promise.allSettled(
      imagesToRemove.map((image) => {
        const name = (image.file as File).name.split("/").at(-1);
        return instance.catalogues.deleteBasketUpload(
          basket.basketToken,
          name ?? "",
          { params: { type: "photo" } },
        );
      }),
    );

    for (const image of images) {
      if (!image.name.includes(basket.basketToken)) {
        await handleImageUpload(basket.basketToken, image as File);
      }
    }

    const hasUpdated = await pingAPIForResult(
      refetchBasket.refetch,
      ({ data }) => {
        if (!!images.length) {
          return !images.some(({ name }) => {
            return !data?.uploads?.find(
              ({ url }) =>
                url.includes(encodeURIComponent(name)) ||
                url.includes(encodeURIComponent(encodeURIComponent(name))), // gross but both ui and api encode the name which causes a double encode issue
            );
          });
        } else {
          return data?.uploads?.length === 0;
        }
      },
      500,
      10,
    );
    if (!hasUpdated) console.error("No uploads found on basket.");

    setUploadLoading(false);
  });

  useEffect(() => {
    if (!!uploads?.length) {
      (async () => {
        try {
          const responses = await Promise.allSettled(
            uploads.map(async ({ url }) => {
              const res = await fetch(url);
              return res.blob();
            }) || [],
          );
          const customImages = uploads.flatMap(({ url }, index) => {
            const response = responses[index];
            if (response && response.status === "fulfilled") {
              const name = new URL(url).pathname.replace("/", "");
              return {
                uri: url,
                id: name.split(".")[0],
                file: new File(
                  [response.value],
                  new URL(url).pathname.replace("/", ""),
                  {
                    type: "image/" + name.split(".")[1],
                  },
                ),
              };
            }
            return [];
          });
          setBasketImages(customImages);
          setImages(customImages.map(({ file }) => file as File));
        } catch (error) {
          console.error(error);
          // silently fail
        }
        setImagesAreLoading(false);
      })();
    }
  }, [uploads]);

  return {
    images,
    basketImages,
    imagesAreLoading,
    setImages: (files) => {
      hasChanged.current = true;
      setImages(files);
    },
    uploadLoading,
    uploadImages,
  };
};
